import React, { useRef, useState } from "react";
import { useFieldArray, useForm, useFormContext, useWatch } from "react-hook-form";
import StyledPhoneInput from "../styled/StyledPhoneInput";
import {
  UserFormData,
  userSchema,
  DEFAULT_USER,
  RegisterFormData,
} from "../../types/forms/auth/registerFormData.form";
import { useCheckEmailAvailability } from "../../api/auth.api";
import { toast } from "react-toastify";
import { ControlledUnifiedInput } from "../ui/customs/controlled-unified-input";
import { Button } from "../ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserForm } from "@/components/shared/user/UserForm";
import { EntityCard } from "./EntityCard";

interface RegisterStepUsersProps {
  onSubmit: () => void;
  onBack: () => void;
}

const RegisterStepUsers: React.FC<RegisterStepUsersProps> = ({ onSubmit, onBack }) => {
  const { control, trigger, getValues, setValue } = useFormContext<RegisterFormData>();
  const { mutateAsync: checkEmailAvailability } = useCheckEmailAvailability();

  const admin = useWatch({ control, name: "admin" as const });

  const [isAdminSaved, setIsAdminSaved] = useState(() => !!(admin.firstName && admin.email));
  const [isAdminEditing, setIsAdminEditing] = useState(() => !(admin.firstName && admin.email));

  const adminSnapshotRef = useRef<typeof admin | null>(null);

  const [stateAdditionalUserForm, setStateAdditionalUserForm] = useState<{
    editIndex: number | undefined;
    show: boolean;
  }>({ editIndex: undefined, show: false });

  const { fields, remove, update, append } = useFieldArray({
    control,
    name: "additionalUsers" as const,
  });

  const additionalUserForm = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_USER,
  });

  const handleSaveAdmin = async () => {
    const isValid = await trigger(["admin"]);
    if (!isValid) return;

    const isEmailAvailable = await checkEmailAvailability({ email: admin.email });
    if (!isEmailAvailable) {
      toast.error("L'email administrateur est déjà utilisé");
      return;
    }

    setIsAdminSaved(true);
    setIsAdminEditing(false);
    window.scrollTo(0, 0);
  };

  const handleEditAdmin = () => {
    adminSnapshotRef.current = getValues("admin");
    setIsAdminEditing(true);
  };

  const handleCancelAdmin = () => {
    const snapshot = adminSnapshotRef.current;
    if (snapshot) {
      (Object.keys(snapshot) as Array<keyof typeof snapshot>).forEach((key) => {
        setValue(`admin.${key}`, snapshot[key]);
      });
    }
    setIsAdminEditing(false);
  };

  const handleSaveAdditionalUser = async (data: UserFormData) => {
    const isEmailAvailable = await checkEmailAvailability({ email: data.email });
    if (!isEmailAvailable) {
      toast.error("L'email est déjà utilisé");
      return;
    }

    if (stateAdditionalUserForm.editIndex !== undefined) {
      update(stateAdditionalUserForm.editIndex, data);
    } else {
      append(data);
    }
    additionalUserForm.reset(DEFAULT_USER);
    setStateAdditionalUserForm({ editIndex: undefined, show: false });
    window.scrollTo(0, 0);
  };

  const handleCancelAdditionalUser = () => {
    setStateAdditionalUserForm({ editIndex: undefined, show: false });
    additionalUserForm.reset(DEFAULT_USER);
  };

  return (
    <div className="flex flex-col gap-8 py-28 md:px-20 lg:px-8">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="font-jakarta-sans text-3xl font-semibold text-black">Créez votre compte</h1>
        <p className="text-base text-gray-600">Entrez vos informations pour créer votre espace.</p>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-blue-600">Super administrateur</h2>
        <EntityCard
          isEditing={isAdminEditing}
          onEdit={handleEditAdmin}
          onSave={handleSaveAdmin}
          onCancel={isAdminSaved ? handleCancelAdmin : undefined}
          summary={
            <>
              <p className="text-sm text-gray-500">Super administrateur</p>
              <p className="font-medium">
                {admin.firstName} {admin.lastName}
              </p>
              <p className="text-sm text-gray-500">{admin.email}</p>
            </>
          }
          form={
            <>
              <ControlledUnifiedInput name="admin.lastName" label="Nom" control={control} />
              <ControlledUnifiedInput name="admin.firstName" label="Prénom" control={control} />
              <ControlledUnifiedInput name="admin.email" label="Email" control={control} />
              <StyledPhoneInput
                name="admin.mobilePhoneNumber"
                title="Téléphone mobile"
                control={control}
                defaultCountry="FR"
              />
              <StyledPhoneInput
                name="admin.fixedPhoneNumber"
                title="Téléphone fixe (facultatif)"
                placeholder="Téléphone fixe (facultatif)"
                control={control}
                defaultCountry="FR"
              />
              <ControlledUnifiedInput
                name="admin.password"
                label="Mot de passe"
                control={control}
                inputType="password"
              />
              <ControlledUnifiedInput
                name="admin.confirmPassword"
                label="Confirmation du mot de passe"
                control={control}
                inputType="password"
              />
            </>
          }
        />
      </div>

      {isAdminSaved ? (
        <>
          <div className="flex flex-col gap-4">
            <h2 className="text-base font-semibold text-blue-600">Utilisateurs additionnels</h2>

            {fields.map((item, index) => {
              const isEditing =
                stateAdditionalUserForm.show && stateAdditionalUserForm.editIndex === index;
              return (
                <EntityCard
                  key={item.id}
                  isEditing={isEditing}
                  onEdit={() => {
                    setStateAdditionalUserForm({ editIndex: index, show: true });
                    additionalUserForm.reset(fields[index]);
                  }}
                  onSave={additionalUserForm.handleSubmit(handleSaveAdditionalUser)}
                  onCancel={handleCancelAdditionalUser}
                  onRemove={() => remove(index)}
                  summary={
                    <>
                      <p className="text-sm text-gray-500">Utilisateur</p>
                      <p className="font-medium">
                        {item.firstName} {item.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{item.email}</p>
                    </>
                  }
                  form={<UserForm control={additionalUserForm.control} showPassword />}
                />
              );
            })}

            {stateAdditionalUserForm.show && stateAdditionalUserForm.editIndex === undefined && (
              <EntityCard
                isEditing
                summary={null}
                form={<UserForm control={additionalUserForm.control} showPassword />}
                onSave={additionalUserForm.handleSubmit(handleSaveAdditionalUser)}
                onCancel={handleCancelAdditionalUser}
              />
            )}

            {!stateAdditionalUserForm.show && (
              <Button
                onClick={() => {
                  setStateAdditionalUserForm({ editIndex: undefined, show: true });
                  additionalUserForm.reset(DEFAULT_USER);
                }}
                variant="tertiary"
              >
                Ajouter un utilisateur
              </Button>
            )}
          </div>

          <div className="flex flex-row justify-end gap-2">
            <Button variant="secondary" onClick={onBack}>
              Retour
            </Button>
            <Button onClick={onSubmit}>Suivant</Button>
          </div>
        </>
      ) : (
        <div className="flex flex-row justify-end gap-2">
          <Button variant="secondary" onClick={onBack}>
            Retour
          </Button>
        </div>
      )}
    </div>
  );
};

export default RegisterStepUsers;
